// Who eats along (spec E1) on the device: works offline, like the rest of Vandaag.

import { useLiveQuery } from "dexie-react-hooks";
import { isoWeekday } from "@shared/time";
import { uuidv7 } from "@shared/uuid";
import { db, getMeta, type Meal, type Person } from "./db";
import type { Me } from "./session";
import { sync } from "./sync";

const now = () => new Date().toISOString();

export function usePeople(): Person[] | undefined {
  return useLiveQuery(async () =>
    (await db.people.toArray()).filter((p) => !p.deleted_at).sort((a, b) => a.position - b.position || a.name.localeCompare(b.name)),
  );
}

/** Person ids per weekday (1 = Monday). */
export function useMealDefaults(): Map<number, string[]> | undefined {
  return useLiveQuery(async () => {
    const rows = (await db.meal_defaults.toArray()).filter((d) => !d.deleted_at);
    const map = new Map<number, string[]>();
    for (const r of rows) map.set(r.weekday, [...(map.get(r.weekday) ?? []), r.person_id]);
    return map;
  });
}

export interface MealView {
  meal: Meal | null;
  place: "home" | "out";
  people: Person[];
}

async function mealOn(date: string): Promise<Meal | null> {
  return (
    (
      await db.meals
        .where("meal_date")
        .equals(date)
        .filter((m) => !m.deleted_at)
        .toArray()
    ).sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1))[0] ?? null
  );
}

/** Who eats along on a date: the meal of that day, or the standard while the day is not made yet. */
export function useMeal(date: string): MealView | undefined {
  return useLiveQuery(async () => {
    const people = (await db.people.toArray()).filter((p) => !p.deleted_at);
    const meal = await mealOn(date);
    let ids: string[];
    if (meal) {
      ids = (
        await db.meal_participants
          .where("meal_id")
          .equals(meal.id)
          .filter((p) => !p.deleted_at)
          .toArray()
      ).map((p) => p.person_id);
    } else {
      ids = (await db.meal_defaults.where("weekday").equals(isoWeekday(date)).toArray())
        .filter((d) => !d.deleted_at)
        .map((d) => d.person_id);
    }
    const chosen = people.filter((p) => ids.includes(p.id)).sort((a, b) => a.position - b.position);
    return { meal, place: meal?.place ?? "home", people: chosen };
  }, [date]);
}

/** Changes who eats along on one day, or "Uit eten" (E1 AC2, AC3). */
export async function setMeal(date: string, place: "home" | "out", personIds: string[]): Promise<void> {
  const me = await getMeta<Me>("me");
  const spaceId = me?.settings?.personal_space_id ?? "";
  const at = now();
  const meal = await mealOn(date);
  const id = meal?.id ?? uuidv7();
  const current = meal ? (await db.meal_participants.where("meal_id").equals(meal.id).toArray()).filter((p) => !p.deleted_at) : [];
  const participants = personIds.map((personId) => ({ id: current.find((c) => c.person_id === personId)?.id ?? uuidv7(), personId }));
  const keep = new Set(participants.map((p) => p.id));
  const rowIds = [id, ...participants.map((p) => p.id), ...current.map((c) => c.id)];
  await sync.mutate("meal.set", { id, date, place, participants, at }, rowIds, async () => {
    if (meal) await db.meals.update(id, { place, edited_at: at, updated_at: at });
    else
      await db.meals.add({
        id,
        space_id: spaceId,
        user_id: me?.user.id ?? "",
        meal_date: date,
        place,
        edited_at: at,
        version: 0,
        deleted_at: null,
        created_at: at,
        updated_at: at,
      });
    for (const c of current) if (!keep.has(c.id)) await db.meal_participants.update(c.id, { deleted_at: at });
    for (const p of participants) {
      if (current.some((c) => c.id === p.id)) continue;
      await db.meal_participants.add({
        id: p.id,
        space_id: spaceId,
        meal_id: id,
        person_id: p.personId,
        version: 0,
        deleted_at: null,
        created_at: at,
        updated_at: at,
      });
    }
  });
}

export async function savePerson(id: string | null, name: string, position: number): Promise<string> {
  const personId = id ?? uuidv7();
  const me = await getMeta<Me>("me");
  const at = now();
  await sync.mutate("person.save", { id: personId, name: name.trim(), position }, [personId], async () => {
    if (id) await db.people.update(id, { name: name.trim(), position });
    else
      await db.people.add({
        id: personId,
        space_id: me?.settings?.personal_space_id ?? "",
        name: name.trim(),
        position,
        seed_key: null,
        version: 0,
        deleted_at: null,
        created_at: at,
        updated_at: at,
      });
  });
  return personId;
}

export async function removePerson(id: string): Promise<void> {
  const at = now();
  const defaults = (await db.meal_defaults.toArray()).filter((d) => d.person_id === id && !d.deleted_at);
  await sync.mutate("person.remove", { id, at }, [id, ...defaults.map((d) => d.id)], async () => {
    await db.people.update(id, { deleted_at: at });
    for (const d of defaults) await db.meal_defaults.update(d.id, { deleted_at: at });
  });
}

/** The standard for a weekday. Meals of open days follow on the server and come back with the next pull (E1 AC2). */
export async function setMealDefaults(weekday: number, personIds: string[]): Promise<void> {
  const me = await getMeta<Me>("me");
  const at = now();
  const current = (await db.meal_defaults.where("weekday").equals(weekday).toArray()).filter((d) => !d.deleted_at);
  const defaults = personIds.map((personId) => ({ id: current.find((c) => c.person_id === personId)?.id ?? uuidv7(), personId }));
  const keep = new Set(defaults.map((d) => d.id));
  await sync.mutate("mealDefaults.set", { weekday, defaults }, [...keep, ...current.map((c) => c.id)], async () => {
    for (const c of current) if (!keep.has(c.id)) await db.meal_defaults.update(c.id, { deleted_at: at });
    for (const d of defaults) {
      if (current.some((c) => c.id === d.id)) continue;
      await db.meal_defaults.add({
        id: d.id,
        space_id: me?.settings?.personal_space_id ?? "",
        user_id: me?.user.id ?? "",
        weekday,
        person_id: d.personId,
        version: 0,
        deleted_at: null,
        created_at: at,
        updated_at: at,
      });
    }
  });
}
